import type { Optional } from '@festina-life/core'
import {
  type PropsWithChildren,
  type ReactNode,
  useCallback,
  useDeferredValue,
  useMemo,
} from 'react'

import { collectAllPaths, pruneTreeWithQuery } from './helpers'
import { type CommonTreeProps, Provider } from './treeContext'
import type { TreeLeafData, TreeNodeData } from './types'
import { useExpandedPaths } from './useExpandedPaths'

type ExternalTreeContextProps<Leaf extends TreeLeafData> = CommonTreeProps & {
  trees: TreeNodeData<Leaf>[]
  renderLeaf: (leaf: Leaf) => ReactNode
  renderNode?: (node: TreeNodeData<Leaf>) => ReactNode
  renderNodeActions?: (node: TreeNodeData<Leaf>) => ReactNode
  renderLeafActions?: (node: Leaf) => ReactNode
  query?: Optional<string>
  onSelectNode?: (node: TreeNodeData<Leaf>) => void
  onSelectLeaf?: (leaf: Leaf) => void
  shouldSortNodes?: boolean
}

const TreeContextProvider = <Leaf extends TreeLeafData>({
  children,
  trees,
  containerRef,
  renderLeaf,
  renderTags,
  id,
  renderNode,
  renderNodeActions,
  renderLeafActions,
  nodeClassName,
  leafClassName,
  query,
  onSelectLeaf,
  onSelectNode,
  shouldSortNodes = false,
  isVisible,
}: PropsWithChildren<ExternalTreeContextProps<Leaf>>) => {
  // Defer the query so the search input stays immediately responsive while
  // the expensive tree filtering and expansion run at lower priority.
  const deferredQuery = useDeferredValue(query)

  const filteredTrees = useMemo(
    () => pruneTreeWithQuery(trees, deferredQuery),
    [deferredQuery, trees],
  )
  const allPaths = useMemo(
    () => collectAllPaths(filteredTrees),
    [filteredTrees],
  )
  const { expandedPaths, togglePath } = useExpandedPaths(allPaths, id)

  // While searching, show all filtered nodes expanded without persisting that
  // state. Fall back to the user's saved paths
  // when there is no active query.

  const effectiveExpandedPaths = deferredQuery
    ? allPaths
    : (expandedPaths ?? [])

  const handleRenderLeaf = useCallback(
    (leaf: TreeLeafData) => renderLeaf(leaf as Leaf),
    [renderLeaf],
  )

  const handleSelectNode = useCallback(
    (node: TreeNodeData<TreeLeafData>) =>
      onSelectNode?.(node as TreeNodeData<Leaf>),
    [onSelectNode],
  )

  const handleSelectLeaf = useCallback(
    (leaf: TreeLeafData) => onSelectLeaf?.(leaf as Leaf),
    [onSelectLeaf],
  )

  const handleRenderNode = useCallback(
    (node: TreeNodeData<TreeLeafData>) =>
      renderNode?.(node as TreeNodeData<Leaf>),
    [renderNode],
  )

  const handleRenderNodeActions = useCallback(
    (node: TreeNodeData<TreeLeafData>) =>
      renderNodeActions?.(node as TreeNodeData<Leaf>),
    [renderNodeActions],
  )

  const handleRenderLeafActions = useCallback(
    (leaf: TreeLeafData) => renderLeafActions?.(leaf as Leaf),
    [renderLeafActions],
  )

  return (
    <Provider
      value={{
        id,
        trees: filteredTrees,
        expandedPaths: effectiveExpandedPaths,
        renderLeaf: handleRenderLeaf,
        renderTags,
        renderNode: renderNode ? handleRenderNode : undefined,
        renderNodeActions: renderNodeActions
          ? handleRenderNodeActions
          : undefined,
        renderLeafActions: renderLeafActions
          ? handleRenderLeafActions
          : undefined,
        onTogglePath: togglePath,
        containerRef,
        nodeClassName,
        leafClassName,
        onSelectLeaf: handleSelectLeaf,
        onSelectNode: handleSelectNode,
        shouldSortNodes,
        isVisible,
      }}
    >
      {children}
    </Provider>
  )
}

export { TreeContextProvider }
